/**
 * NAPTAN Bus Stop Client
 * Loads pre-bundled Chelmsford bus stops from local JSON
 *
 * Note: The NAPTAN API doesn't support CORS, so we pre-bundle
 * the bus stop data. Run `npm run update-stops` to update
 * (also runs weekly via .github/workflows/update-stops.yml).
 */

import { z } from 'zod';
import { Logger } from '@utils/logger';
import { retryWithBackoff } from '@utils/helpers';
import type { BusStop } from '@/types';
import { BusStopSchema } from '@/types';

// Schema for the JSON array
const BusStopsArraySchema = z.array(BusStopSchema);

/**
 * Fetch Chelmsford bus stops from pre-bundled JSON
 * The data is generated by scripts/process-stops.js from NAPTAN CSV,
 * filtered to stops with a current BODS timetabled service
 */
export async function fetchChelmsfordBusStops(): Promise<BusStop[]> {
    Logger.info('Loading Chelmsford bus stops from local data');

    const stops: unknown = await retryWithBackoff(
        async () => {
            const res = await fetch('/bus-stops.min.json');
            if (!res.ok) {
                throw new Error(`Failed to load bus stops: ${res.status}`);
            }
            return res.json() as Promise<unknown>;
        },
        3,
        1000
    );

    const validated = BusStopsArraySchema.safeParse(stops);
    if (!validated.success) {
        Logger.error('Invalid bus stops data', validated.error);
        throw new Error('Invalid bus stops data');
    }

    Logger.success(`Loaded ${validated.data.length} bus stops`);
    return validated.data;
}
